import { logger } from '@adornis/base/logging.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, of, switchMap, timer } from 'rxjs';
import { OSCEGroup } from '../db/osce-group.js';
import './ils-editor-multi-osce-type-picker.js';

/**
 * @element ils-editor-osce-group
 */
@customElement('ils-editor-osce-group')
export class IlsEditorOsceGroup extends ChemistryLitElement {
  @property({ attribute: 'osce-group-id', type: String }) osceGroupID = new RXController<string | null>(this, null);

  @state() private readonly _osceGroup = new RXController(this, this.osceGroupID.observable, osceGroupID$ =>
    osceGroupID$.pipe(
      switchMap(osceGroupID =>
        osceGroupID
          ? subscribeByID(OSCEGroup, osceGroupID)({ name: 1, osceTypes: 1, examType: 1, archived: 1 })
          : of(null),
      ),
    ),
  );
  @state() private readonly _form = new FormController(this, this._osceGroup.observable, {
    success: async osceGroup => {
      try {
        await XDialog.waitFor(Promise.all([firstValueFrom(timer(250)), osceGroup.save()]));
        void XSnackbar.show('OSCE-Gruppe wurde erfolgreich gespeichert');
      } catch (e) {
        logger.error({ err: e }, 'Es gab ein Problem beim Speichern der OSCE-Gruppe');
        void XDialog.alert('Es gab ein Problem beim Speichern der OSCE-Gruppe. Bitte versuchen Sie es später erneut.');
      }
    },
  });

  override render() {
    if (this._osceGroup.isLoading)
      return html`
        <x-flex space="md" padding="md" center crossaxis-center ${css({ minHeight: '200px' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    if (!this._osceGroup.value)
      return html`
        <x-flex space="md" padding="md" center crossaxis-center>
          <x-text> OSCE-Gruppe nicht gefunden </x-text>
        </x-flex>
      `;
    const maxWidth = `${1000 + 32}` + 'px';
    return html`
      <x-flex space="md" padding="md" ${css({ paddingBottom: this.spacing.lg, maxWidth })}>
        <x-flex space="md">
          <x-flex horizontal crossaxis-center space="md">
            <x-flex flex horizontal crossaxis-center space="sm">
              <x-button
                mode="icon"
                icon="arrow_back"
                @click=${() => {
                  this._osceGroup.value?.revert();
                  try {
                    window.history.back();
                  } catch {
                    goTo({ path: '/start/editor-osce', searchParams: NO_CHANGE });
                  }
                }}
              ></x-button>
              <x-h2> OSCE-Gruppe bearbeiten </x-h2>
            </x-flex>
            <x-button
              end
              mode="outline"
              @click="${async () => {
                if (!this._osceGroup.value) return;
                if (!(await XDialog.confirm('Soll diese OSCE-Gruppe wirklich archiviert werden?'))) return;
                try {
                  this._osceGroup.value.archived = true;
                  await XDialog.waitFor(Promise.all([firstValueFrom(timer(250)), this._osceGroup.value.save()]));
                  void XSnackbar.show('OSCE-Gruppe erfolgreich archiviert');
                  goTo({ path: '/start/editor-osce', searchParams: NO_CHANGE });
                } catch (e) {
                  logger.error({ err: e }, 'Es gab ein Problem beim Archivieren dieser OSCE-Gruppe');
                  void XDialog.alert(
                    'Es gab ein Problem beim Archivieren dieser OSCE-Gruppe. Bitte versuchen Sie es später erneut.',
                  );
                }
              }}"
            >
              OSCE-Gruppe archivieren
            </x-button>
            <x-button end @click=${(e: MouseEvent) => this._form.validate({ mouseEvent: e })}>
              Änderungen speichern
            </x-button>
          </x-flex>
          <x-input flex placeholder="Name" ${this._form.field('name')}></x-input>
          <x-flex space="sm">
            <x-text> OSCE-Typen festlegen: </x-text>
            <ils-editor-multi-osce-type-picker
              exam-type=${this._osceGroup.value.examType}
              ${this._form.field('osceTypes')}
            ></ils-editor-multi-osce-type-picker>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }
}
